import type { AgentDto, ApiKeyDto, CreatedApiKey, Role, UsageRow } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { CopyField } from '@/components/copy-field';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { api, ApiError, errorMessage } from '@/lib/api';
import {
  COST_ESTIMATE_NOTE,
  costCell,
  formatCompact,
  formatDate,
  formatDateTime,
} from '@/lib/format';
import { usePaged } from '@/lib/queries';

function keyStatus(k: ApiKeyDto): {
  label: string;
  variant: 'secondary' | 'outline' | 'destructive';
} {
  if (k.revokedAt) return { label: 'Revoked', variant: 'destructive' };
  if (k.expiresAt && new Date(k.expiresAt) <= new Date())
    return { label: 'Expired', variant: 'outline' };
  return { label: 'Active', variant: 'secondary' };
}

function NewKeyDialog({ tenantId, agents }: { tenantId: string; agents: AgentDto[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [allAgents, setAllAgents] = useState(true);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [metadata, setMetadata] = useState('{}');
  const [expires, setExpires] = useState('');
  const [metadataError, setMetadataError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (meta: Record<string, unknown>) =>
      api<CreatedApiKey>('/api-keys', {
        method: 'POST',
        body: {
          name,
          allAgents,
          agentIds: allAgents ? [] : agentIds,
          metadata: meta,
          ...(expires ? { expiresAt: new Date(`${expires}T23:59:59`).toISOString() } : {}),
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['api-keys', tenantId] }),
  });
  const fieldErr = (path: string) =>
    create.error instanceof ApiError ? create.error.fieldError(path) : undefined;

  const reset = () => {
    setName('');
    setAllAgents(true);
    setAgentIds([]);
    setMetadata('{}');
    setExpires('');
    setMetadataError(null);
    create.reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New API key</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create an API key</DialogTitle>
          <DialogDescription>
            Give each client its own key so you can revoke it on its own.
          </DialogDescription>
        </DialogHeader>
        {create.data ? (
          <div className="grid gap-3">
            <Alert>
              <AlertDescription>
                Copy this key now. It is shown only once; only a hash of it is stored.
              </AlertDescription>
            </Alert>
            <CopyField value={create.data.key} label="API key" />
            <DialogFooter>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              try {
                const parsed: unknown = JSON.parse(metadata || '{}');
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
                  throw new Error();
                setMetadataError(null);
                create.mutate(parsed as Record<string, unknown>);
              } catch {
                setMetadataError('Metadata must be a JSON object, e.g. {"plan": "pro"}');
              }
            }}
          >
            <ErrorAlert error={create.error} />
            <div className="grid gap-2">
              <Label htmlFor="key-name">Name</Label>
              <Input
                id="key-name"
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mobile app"
              />
            </div>
            <fieldset className="grid gap-2">
              <legend className="mb-2 text-sm font-medium">Agents this key can call</legend>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="scope"
                  checked={allAgents}
                  onChange={() => setAllAgents(true)}
                />
                All agents
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="scope"
                  checked={!allAgents}
                  onChange={() => setAllAgents(false)}
                />
                Only selected agents
              </label>
              {!allAgents && (
                <div className="ml-6 grid max-h-40 gap-1 overflow-auto">
                  {agents.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={agentIds.includes(a.id)}
                        onChange={(e) =>
                          setAgentIds((ids) =>
                            e.target.checked ? [...ids, a.id] : ids.filter((i) => i !== a.id),
                          )
                        }
                      />
                      {a.name}
                    </label>
                  ))}
                  {fieldErr('agentIds') && (
                    <p className="text-destructive text-sm">{fieldErr('agentIds')}</p>
                  )}
                </div>
              )}
            </fieldset>
            <div className="grid gap-2">
              <Label htmlFor="key-metadata">Metadata (JSON)</Label>
              <Textarea
                id="key-metadata"
                className="font-mono text-xs"
                value={metadata}
                onChange={(e) => setMetadata(e.target.value)}
              />
              <p
                className={
                  metadataError ? 'text-destructive text-sm' : 'text-muted-foreground text-xs'
                }
              >
                {metadataError ?? 'Request mappings can read this, e.g. a customer id or plan.'}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="key-expires">Expires (optional)</Label>
              <Input
                id="key-expires"
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
              {fieldErr('expiresAt') && (
                <p className="text-destructive text-sm">{fieldErr('expiresAt')}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create key'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApiKeysContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const queryClient = useQueryClient();
  const isOwner = hasRole(role, 'owner');
  const keys = usePaged<ApiKeyDto>(['api-keys', tenantId], '/api-keys');
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  // All-time spend per key (the playground's runs have no key and are not listed here).
  const usage = useQuery({
    queryKey: ['usage', tenantId, 'apiKey', 'all'],
    queryFn: () => api<{ items: UsageRow[] }>('/usage?groupBy=apiKey'),
  });
  const usageOf = (id: string) => usage.data?.items.find((u) => u.key === id);
  const [revoking, setRevoking] = useState<ApiKeyDto | null>(null);
  // One revoked key, or every revoked key at once.
  const [deleting, setDeleting] = useState<ApiKeyDto | 'all-revoked' | null>(null);
  const rows = keys.data?.pages.flatMap((p) => p.items) ?? [];
  const revokedCount = rows.filter((k) => k.revokedAt).length;
  const agentList = agents.data?.pages.flatMap((p) => p.items) ?? [];
  const agentName = (id: string) => agentList.find((a) => a.id === id)?.name ?? 'Deleted agent';
  const search = useListSearch(rows, (k) => [
    k.name,
    `ap_live_${k.prefix}`,
    k.allAgents ? 'All agents' : k.agentIds.map(agentName).join(' '),
    keyStatus(k).label,
  ]);

  const revoke = useMutation({
    mutationFn: (id: string) => api<ApiKeyDto>(`/api-keys/${id}/revoke`, { method: 'POST' }),
    onSuccess: (k) => {
      toast.success(`${k.name} revoked`);
      void queryClient.invalidateQueries({ queryKey: ['api-keys', tenantId] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = async () => {
    if (deleting === 'all-revoked') {
      const { deleted } = await api<{ deleted: number }>('/api-keys/revoked', {
        method: 'DELETE',
      });
      toast.success(`Deleted ${deleted} revoked ${deleted === 1 ? 'key' : 'keys'}`);
    } else if (deleting) {
      await api(`/api-keys/${deleting.id}`, { method: 'DELETE' });
      toast.success(`${deleting.name} deleted`);
    }
    await queryClient.invalidateQueries({ queryKey: ['api-keys', tenantId] });
  };

  return (
    <>
      <PageHeader
        title="API keys"
        description="Clients send a key with every call to an agent endpoint. Revoking a key takes effect immediately; a revoked key can then be deleted."
        actions={
          isOwner && (
            <>
              {revokedCount > 0 && (
                <Button variant="outline" onClick={() => setDeleting('all-revoked')}>
                  Delete revoked ({revokedCount})
                </Button>
              )}
              <NewKeyDialog tenantId={tenantId} agents={agentList} />
            </>
          )
        }
      />
      <ErrorAlert error={keys.error} />
      {!isOwner && (
        <p className="text-muted-foreground mb-4 text-sm">
          Only tenant owners can create, revoke or delete keys.
        </p>
      )}
      {!keys.isPending && rows.length === 0 ? (
        <EmptyState title="No API keys yet">
          Create a key for each client that will call your agents.
        </EmptyState>
      ) : (
        <div>
          <ListSearch
            className="mb-4"
            value={search.query}
            onChange={search.setQuery}
            label="Search API keys"
            placeholder="Search by name, key prefix or agent"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Agents</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                {isOwner && <TableHead className="sr-only">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((k) => {
                const status = keyStatus(k);
                return (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs">ap_live_{k.prefix}_…</TableCell>
                    <TableCell className="text-sm">
                      {k.allAgents ? 'All agents' : k.agentIds.map(agentName).join(', ')}
                    </TableCell>
                    {(() => {
                      const u = usageOf(k.id);
                      const cost = u ? costCell(u.costUsd, u.unpricedRuns) : null;
                      return (
                        <>
                          <TableCell className="text-right text-sm tabular-nums">
                            {u ? u.runs.toLocaleString() : usage.isPending ? '' : '0'}
                          </TableCell>
                          <TableCell
                            className="text-right text-sm tabular-nums"
                            title={
                              u
                                ? `${u.inputTokens.toLocaleString()} in / ${u.outputTokens.toLocaleString()} out`
                                : undefined
                            }
                          >
                            {u
                              ? formatCompact(u.inputTokens + u.outputTokens)
                              : usage.isPending
                                ? ''
                                : '—'}
                          </TableCell>
                          <TableCell
                            className="text-right text-sm tabular-nums"
                            title={cost?.title ?? COST_ESTIMATE_NOTE}
                          >
                            {cost ? cost.text : usage.isPending ? '' : '—'}
                          </TableCell>
                        </>
                      );
                    })()}
                    <TableCell className="text-muted-foreground text-sm">
                      {k.lastUsedAt ? formatDateTime(k.lastUsedAt) : 'Never'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {k.expiresAt ? formatDate(k.expiresAt) : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </TableCell>
                    {isOwner && (
                      <TableCell className="text-right">
                        {k.revokedAt ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleting(k)}
                            aria-label={`Delete ${k.name}`}
                          >
                            Delete
                          </Button>
                        ) : (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setRevoking(k)}
                            aria-label={`Revoke ${k.name}`}
                          >
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {rows.length > 0 && search.filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isOwner ? 10 : 9}>
                    <NoMatches query={search.query} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={Boolean(revoking)} onOpenChange={(open) => !open && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {revoking?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Clients using this key get 401 from their next call. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              onClick={() => revoking && revoke.mutate(revoking.id)}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={
          deleting === 'all-revoked'
            ? 'Delete all revoked keys?'
            : `Delete ${deleting?.name ?? 'this key'}?`
        }
        description={
          deleting === 'all-revoked'
            ? 'Every revoked key is removed from this list. Past runs and threads stay; their caller shows as a deleted key.'
            : 'The revoked key is removed from this list. Past runs and threads stay; their caller shows as a deleted key.'
        }
        onConfirm={remove}
      />
    </>
  );
}

export function ApiKeysPage() {
  return (
    <RequireTenant>
      {(tenant) => <ApiKeysContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
